import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { contactAPI } from '../../services/api';
import { ContactSkeleton } from '../../components/Skeleton/PageSkeletons';

const ContactContainer = styled.div`
  padding: 80px 24px 24px;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 400;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 8px;
`;

const PageDescription = styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const ContactForm = styled(motion.form)`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 14px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.15s ease;
  
  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.focus};
    box-shadow: 0 0 0 2px rgba(0, 115, 187, 0.2);
  }
  
  &:invalid {
    border-color: ${props => props.theme.colors.error};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 14px;
  font-family: ${props => props.theme.fonts.primary};
  min-height: 120px;
  resize: vertical;
  transition: all 0.15s ease;
  
  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.focus};
    box-shadow: 0 0 0 2px rgba(0, 115, 187, 0.2);
  }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primaryHover};
    border-color: ${props => props.theme.colors.primaryHover};
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.focus};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContactInfo = styled(motion.div)`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 32px;
`;

const ContactInfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  
  svg {
    width: 18px;
    height: 18px;
    color: ${props => props.theme.colors.primary};
    margin-top: 2px;
    flex-shrink: 0;
  }
  
  div {
    h4 {
      font-size: 14px;
      font-weight: 500;
      color: ${props => props.theme.colors.textPrimary};
      margin: 0 0 4px 0;
    }
    
    p {
      font-size: 14px;
      color: ${props => props.theme.colors.textSecondary};
      margin: 0;
      line-height: 1.4;
    }
  }
`;

const Message = styled(motion.div)`
  padding: 12px 16px;
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  
  ${props => props.type === 'success' && `
    background-color: rgba(3, 127, 12, 0.1);
    color: ${props.theme.colors.success};
    border: 1px solid rgba(3, 127, 12, 0.2);
  `}
  
  ${props => props.type === 'error' && `
    background-color: rgba(217, 21, 21, 0.1);
    color: ${props.theme.colors.error};
    border: 1px solid rgba(217, 21, 21, 0.2);
  `}
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ResponseTimeSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${props => props.theme.colors.border};
  
  h4 {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin-bottom: 8px;
  }
  
  p {
    font-size: 13px;
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.5;
    margin: 0;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    // Simulate initial page load
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await contactAPI.sendMessage(formData);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  if (pageLoading) {
    return <ContactSkeleton />;
  }

  return (
    <ContactContainer>
      <Content>
        <PageHeader>
          <PageTitle>Get In Touch</PageTitle>
          <PageDescription>
            Have a project in mind or want to collaborate? I'd love to hear from you. 
            Let's discuss how we can work together to build something amazing!
          </PageDescription>
        </PageHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <ContactGrid>
            <ContactForm
              onSubmit={handleSubmit}
              variants={itemVariants}
            >
              {message && (
                <Message 
                  type={message.type}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  {message.type === 'success' ? <CheckCircle /> : <AlertCircle />}
                  {message.text}
                </Message>
              )}

              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Project inquiry, collaboration, etc."
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell me about your project, timeline, and requirements..."
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
                <Send />
              </SubmitButton>
            </ContactForm>

            <ContactInfo variants={itemVariants}>
              <ContactInfoTitle>Contact Information</ContactInfoTitle>
              
              <InfoItem>
                <Mail />
                <div>
                  <h4>Email</h4>
                  <p>hassan.m.8537@gmail.com</p>
                </div>
              </InfoItem>

              <InfoItem>
                <Phone />
                <div>
                  <h4>Phone</h4>
                  <p>+923112213827</p>
                </div>
              </InfoItem>

              <InfoItem>
                <MapPin />
                <div>
                  <h4>Location</h4>
                  <p>Karachi, Pakistan</p>
                </div>
              </InfoItem>

              <ResponseTimeSection>
                <h4>Response Time</h4>
                <p>
                  I typically respond to inquiries within 24-48 hours. For urgent matters, 
                  please include "URGENT" in your subject line.
                </p>
              </ResponseTimeSection>
            </ContactInfo>
          </ContactGrid>
        </motion.div>
      </Content>
    </ContactContainer>
  );
};

export default Contact;